import { getToken } from './auth';

declare const __BACKEND_URL__: string;
const API_BASE = __BACKEND_URL__ || '';
const BASE = `${API_BASE}/games`;

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return { Authorization: `Bearer ${token}` };
}

export type GameMode = 'pvp' | 'vs_computer';

export interface GameSummary {
  gameId: string;
  status: string;
  mode: GameMode;
  computerLevel: number | null;
  createdAt: string;
  moveCount: number;
  playerColor: 'w' | 'b' | null;
  waitingForOpponent: boolean;
  turn: 'w' | 'b';
}

export interface MoveRecord {
  moveNumber: number;
  from: string;
  to: string;
  san: string;
  fenAfter: string;
  playedAt: string;
}

export interface GameState {
  gameId: string;
  fen: string;
  turn: 'w' | 'b';
  status: string;
  mode: GameMode;
  computerLevel: number | null;
  inviteCode: string | null;
  playerColor: 'w' | 'b' | null;
  waitingForOpponent: boolean;
  moves: MoveRecord[];
}

export interface MoveResult {
  fen: string;
  turn: 'w' | 'b';
  status: string;
  move: { moveNumber: number; san: string; fenAfter: string };
  computerMove: { san: string; from: string; to: string } | null;
}

export async function createGame(
  mode: GameMode,
  computerLevel?: number,
  openingMoves?: { from: string; to: string; promotion?: string }[],
  playerSide?: 'white' | 'black'
): Promise<GameState> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ mode, computerLevel, openingMoves, playerSide }),
  });
  if (!res.ok) throw new Error('Failed to create game');
  return res.json();
}

function handleUnauthorized(res: Response): void {
  if (res.status === 401) {
    // Token expired or revoked — reload to re-authenticate
    window.location.reload();
  }
}

export async function listGames(): Promise<GameSummary[]> {
  const res = await fetch(BASE, { headers: await authHeaders() });
  if (!res.ok) { handleUnauthorized(res); throw new Error('Failed to list games'); }
  return res.json();
}

export async function getGame(gameId: string): Promise<GameState> {
  const res = await fetch(`${BASE}/${gameId}`, { headers: await authHeaders() });
  if (!res.ok) { handleUnauthorized(res); throw new Error('Failed to get game'); }
  return res.json();
}

export async function postMove(gameId: string, from: string, to: string): Promise<MoveResult> {
  const res = await fetch(`${BASE}/${gameId}/moves`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ from, to }),
  });
  if (!res.ok) {
    handleUnauthorized(res);
    let errorMsg = 'Invalid move';
    try { const d = await res.json(); errorMsg = d.error ?? errorMsg; } catch {}
    throw new Error(errorMsg);
  }
  return res.json();
}

export async function undoMove(gameId: string): Promise<GameState> {
  const res = await fetch(`${BASE}/${gameId}/undo`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to undo move');
  }
  return res.json();
}

export async function truncateMoves(gameId: string, keepMoveCount: number): Promise<GameState> {
  const res = await fetch(`${BASE}/${gameId}/truncate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ keepMoveCount }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to truncate game');
  }
  return res.json();
}

export async function resignGame(gameId: string): Promise<void> {
  await fetch(`${BASE}/${gameId}`, { method: 'DELETE', headers: await authHeaders() });
}

export async function joinGame(inviteCode: string): Promise<GameState> {
  const res = await fetch(`${BASE}/join/${inviteCode}`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to join game');
  }
  return res.json();
}

export async function pingBackend(): Promise<void> {
  const res = await fetch(`${API_BASE}/health`);
  if (!res.ok) throw new Error('backend not ready');
  const data = await res.json() as { ok?: boolean }; // throws if HTML returned (SPA fallback)
  if (!data.ok) throw new Error('backend not ready');
}

export async function getMe(): Promise<{ premium: boolean }> {
  const res = await fetch(`${API_BASE}/me`, { headers: await authHeaders() });
  if (res.status === 404) {
    const { logout } = await import('./auth');
    await logout();
    throw new Error('User not found');
  }
  if (!res.ok) throw new Error('Failed to fetch user info');
  return res.json();
}

export interface UserProfileData { displayName: string; piece: string; color: string; }

export async function getProfile(): Promise<UserProfileData | null> {
  const res = await fetch(`${API_BASE}/me/profile`, { headers: await authHeaders() });
  if (!res.ok) return null;
  return res.json();
}

export async function saveProfile(data: UserProfileData): Promise<UserProfileData> {
  const res = await fetch(`${API_BASE}/me/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to save profile');
  return res.json();
}

export interface AnalysisResult {
  scoreCp: number;
  bestMove: string;
  bestMoveSan: string | null;
  bestMovePosition: { fen: string; from: string; to: string; san: string } | null;
  moveQuality: 'excellent' | 'good' | 'inaccuracy' | 'mistake' | 'blunder' | null;
  evalDropCp: number | null;
  mateIn: number | null;
  alternatives: Array<{ moveSan: string; scoreCp: number; mateIn: number | null }>;
  pv: string | null;
  pvPositions: Array<{ fen: string; from: string; to: string; san: string }>;
  pvStartMoveNum: number;
  pvStartWhite: boolean;
  coachMessage: string;
}

export async function analyzePosition(fen: string, previousFen?: string, playerMoveSan?: string, isOpponent?: boolean): Promise<AnalysisResult> {
  const res = await fetch(`${API_BASE}/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ fen, previousFen, playerMoveSan, isOpponent }),
  });
  if (!res.ok) throw new Error('Analysis failed');
  return res.json();
}

export interface CoachRequest {
  playerMoveSan: string | null;
  moveQuality: string | null;
  evalDropCp: number | null;
  scoreCp: number;
  bestMoveSan: string | null;
  mateIn: number | null;
  alternatives: Array<{ moveSan: string; scoreCp: number; mateIn: number | null }>;
  pv: string | null;
  isOpponent?: boolean;
  openingName?: string | null;
  openingEco?: string | null;
}

export async function requestCoachMessage(input: CoachRequest): Promise<string> {
  const res = await fetch(`${API_BASE}/coach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error('Coach unavailable');
  const data = await res.json() as { coachMessage: string };
  return data.coachMessage;
}

export async function createCheckoutSession(promoCode?: string): Promise<string> {
  const res = await fetch(`${API_BASE}/checkout`, {
    method: 'POST',
    headers: { ...await authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ promoCode }),
  });
  const data = await res.json() as { url: string; error?: string };
  if (!res.ok) throw new Error(data.error || 'Failed to create checkout session');
  return data.url;
}
